import type { ComponentType, SVGProps } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChartPieIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  PhotoIcon,
  AdjustmentsHorizontalIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  HomeModernIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useAuth } from '../../lib/auth';

type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  section?: string;
};

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps): JSX.Element {
  const location = useLocation();
  const { user } = useAuth();

  const baseNav: NavItem[] = [
    { to: '/app', label: 'Dashboard', icon: ChartPieIcon },
    { to: '/app/properties', label: 'Properties', icon: HomeModernIcon },
    { to: '/app/items', label: 'Inspections', icon: PhotoIcon },
    { to: '/app/reports', label: 'Reports', icon: DocumentTextIcon },
    { to: '/app/report-presets', label: 'Templates', icon: DocumentDuplicateIcon },
    { to: '/app/activity', label: 'Activity', icon: ClipboardDocumentListIcon },
    { to: '/app/billing', label: 'Billing & plan', icon: BanknotesIcon },
    { to: '/app/settings', label: 'Settings', icon: Cog6ToothIcon }
  ];

  const superAdminNav: NavItem[] = user?.role === 'superadmin'
    ? [
        { to: '/app/admin', label: 'Admin dashboard', icon: ShieldCheckIcon, section: 'Super admin' },
        { to: '/app/admin/users', label: 'Users', icon: AdjustmentsHorizontalIcon, section: 'Super admin' },
        { to: '/app/admin/settings', label: 'Platform settings', icon: Cog6ToothIcon, section: 'Super admin' }
      ]
    : [];

  const navItems: NavItem[] = [...baseNav, ...superAdminNav];

  return (
    <aside
      className={clsx(
        'glass fixed inset-y-0 left-0 z-40 w-72 border border-white/20 bg-transparent transition-transform duration-300 ease-out dark:border-white/10',
        isOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'
      )}
    >
      <div className="flex h-full flex-col gap-6 px-6 py-8">
        <div className="glass-card flex items-center justify-between px-4 py-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Portfolio</p>
            <p className="text-lg font-semibold">{user?.name ?? 'Team'}</p>
          </div>
          <button
            type="button"
            className="focus-ring sm:hidden"
            onClick={onClose}
            aria-label="Close navigation"
          >
            ✕
          </button>
        </div>

        <nav className="flex-1 space-y-2 text-sm">
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
            const previousItem = index > 0 ? navItems[index - 1] : null;
            const showSectionLabel = item.section && item.section !== previousItem?.section;

            return (
              <div key={item.to} className="space-y-2">
                {showSectionLabel && <p className="px-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">{item.section}</p>}
                <NavLink
                  to={item.to}
                  className={clsx(
                    'focus-ring flex items-center gap-3 rounded-xl px-4 py-3 font-medium transition',
                    active
                      ? 'bg-[hsla(var(--primary)/0.15)] text-primary shadow-inner'
                      : 'text-muted-foreground hover:bg-[hsla(var(--primary)/0.08)] hover:text-foreground'
                  )}
                  onClick={onClose}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </NavLink>
              </div>
            );
          })}
        </nav>

        <div className="glass-card bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 p-5 text-sm">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Trial status</p>
          <p className="mt-2 text-base font-semibold text-foreground">Complimentary starter plan</p>
          <p className="mt-1 text-muted-foreground">
            You are running InspectAI with a complimentary starter trial. Generate one full report, then upgrade anytime.
          </p>
          <NavLink
            to="/app/billing"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            onClick={onClose}
          >
            Manage billing →
          </NavLink>
        </div>
      </div>
    </aside>
  );
}
